/* Fittings finder — guided pipe-fitting selector.
 * Steps: Material (PVC / Brass / ProPress / MegaPress) -> Size -> [PVC only: Schedule] -> fittings.
 * Mirrors the staff Fittings builder in the app: Brass lists nipples and other
 * brass fittings together (no type step), ProPress and MegaPress have no type step, PVC splits
 * by Schedule 40 / 80.
 *
 * Data: one CDN-cached call to the app's /api/storefront/fittings, which
 * returns every ACTIVE fitting in those four materials already normalized
 * (material, diameters, schedule) with quote-only prices zeroed server-side.
 *
 * Buying: purchasable single-variant fittings AJAX-add to the cart through the
 * theme's <product-form> (same markup as the repair-parts finder); quote-only
 * ones dispatch `quote:add` to the global quote drawer; multi-variant ones go
 * to their product page to pick an option.
 */
if (!customElements.get('fittings-finder')) {
  const MATERIALS = {
    PVC: 'Schedule 40 & 80 fittings',
    Brass: 'Nipples, tees, unions, elbows & more',
    ProPress: 'Press-connect copper fittings',
    MegaPress: 'Press-connect fittings for steel pipe',
  };
  // Only PVC asks which type; the order the schedules are offered in.
  const TYPE_STEP_MATERIALS = ['PVC'];
  const TYPE_ORDER = ['Schedule 40', 'Schedule 80'];
  const OTHER_TYPE = 'Other';

  class FittingsFinder extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.materials = [];
      this.state = { material: null, size: null, type: null, filter: '' };
    }

    connectedCallback() {
      this.apiBase = (this.dataset.apiBase || 'https://app.thebackflowdepot.com').replace(/\/$/, '');
      this.cartAddUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add';
      this.innerHTML = '<p class="repair-parts-finder__loading text-center">Loading fittings…</p>';
      this.load();
    }

    async load() {
      try {
        const res = await fetch(`${this.apiBase}/api/storefront/fittings`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'failed');
        this.products = (data.products || []).filter((p) => (p.variants || []).length && (p.diameters || []).length);
        const have = new Set(this.products.map((p) => p.material));
        this.materials = (data.materials || Object.keys(MATERIALS)).filter((m) => have.has(m));
        this.render();
      } catch (e) {
        this.innerHTML = '<p class="repair-parts-finder__loading text-center">Couldn’t load fittings. <a href="/collections/fittings">Browse all fittings</a> or call <strong>(916) 251-7071</strong> and we’ll help.</p>';
      }
    }

    /* ---- Data ---- */

    countFor(material) { return this.products.filter((p) => p.material === material).length; }

    sizesFor(material) {
      const counts = new Map();
      for (const p of this.products) {
        if (p.material !== material) continue;
        for (const d of p.diameters) counts.set(d, (counts.get(d) || 0) + 1);
      }
      return [...counts.entries()].sort((a, b) => this.sizeValue(a[0]) - this.sizeValue(b[0]));
    }

    hasTypeStep(material) { return TYPE_STEP_MATERIALS.includes(material); }

    typeKey(p) { return p.pathType || OTHER_TYPE; }

    inSize(p, material, size) { return p.material === material && p.diameters.includes(size); }

    typesFor(material, size) {
      const counts = new Map();
      for (const p of this.products) {
        if (!this.inSize(p, material, size)) continue;
        const k = this.typeKey(p);
        counts.set(k, (counts.get(k) || 0) + 1);
      }
      const rank = (k) => (TYPE_ORDER.includes(k) ? TYPE_ORDER.indexOf(k) : TYPE_ORDER.length);
      return [...counts.entries()].sort((a, b) => rank(a[0]) - rank(b[0]));
    }

    results() {
      const { material, size, type, filter } = this.state;
      const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
      return this.products.filter((p) => {
        if (!this.inSize(p, material, size)) return false;
        if (type && this.typeKey(p) !== type) return false;
        if (!words.length) return true;
        const hay = `${p.title} ${p.fittingType || ''} ${(p.variants || []).map((v) => v.sku || '').join(' ')}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }

    /* ---- Render ----
       Same markup and classes as the repair parts finder (repair-parts-finder.css),
       so every finder looks like one family: sticky step bar, one stage card at
       a time, then the product-card grid. */

    step() {
      const s = this.state;
      if (!s.material) return 'material';
      if (!s.size) return 'size';
      if (this.hasTypeStep(s.material) && !s.type && this.typesFor(s.material, s.size).length > 1) return 'type';
      return 'results';
    }

    setState(patch) {
      Object.assign(this.state, patch);
      this.render();
      const top = this.getBoundingClientRect().top;
      if (top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    pillsHtml(step) {
      const s = this.state;
      const showType = s.material ? this.hasTypeStep(s.material) : true;
      const typeReachable = !!s.size && this.typesFor(s.material, s.size).length > 1;
      const pill = (key, n, label, value, reachable) => {
        const cls = `repair-parts-finder__pill${step === key ? ' is-active' : ''}${value ? ' is-set' : ''}`;
        return `<button type="button" class="${cls}" data-goto="${key}"${reachable ? '' : ' disabled'}>${n}. ${label}${value ? `<span>: ${value}</span>` : ''}</button>`;
      };
      const sep = '<span class="repair-parts-finder__trail-sep" aria-hidden="true">&rsaquo;</span>';
      let n = 1;
      const items = [
        pill('material', n++, 'Material', s.material ? this.esc(s.material) : '', true),
        pill('size', n++, 'Size', s.size ? this.sizeLabel(s.size) : '', !!s.material),
      ];
      if (showType) items.push(pill('type', n++, 'Type', s.type ? this.esc(s.type === OTHER_TYPE ? 'Other' : s.type) : '', typeReachable));
      const count = step === 'results' ? `<span> (${this.results().length})</span>` : '';
      items.push(`<span class="repair-parts-finder__pill repair-parts-finder__pill--static${step === 'results' ? ' is-active' : ''}">${n}. Fittings${count}</span>`);
      return items.join(sep);
    }

    render() {
      const s = this.state;
      const step = this.step();

      let body = '';
      if (step === 'material') {
        body = this.materials.length
          ? `<div class="repair-parts-finder__stage-card">
              <div class="repair-parts-finder__stage-heading">
                <h2>Choose a material</h2>
                <p>What the pipe is made of — PVC, brass, or press-connect copper or steel.</p>
              </div>
              <div class="repair-parts-finder__brand-grid">${this.materials.map((m) => `
                <button type="button" class="repair-parts-finder__brand-card repair-parts-finder__brand-card--text" data-material="${this.esc(m)}">
                  <span class="repair-parts-finder__tile-title">${this.esc(m)}</span>
                  <span class="repair-parts-finder__tile-desc">${MATERIALS[m] || ''}</span>
                  <span class="repair-parts-finder__tile-count">${this.countFor(m)} fittings</span>
                </button>`).join('')}</div>
            </div>`
          : '<p class="repair-parts-finder__empty-message">No fittings found. Please call <strong>(916) 251-7071</strong>.</p>';
      } else if (step === 'size') {
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
              <div>
                <h2>What size <em>${this.esc(s.material)}</em> fitting?</h2>
                <p>The pipe diameter, in inches.</p>
              </div>
              <button type="button" class="repair-parts-finder__back-link" data-goto="material">Change material</button>
            </div>
            <div class="repair-parts-finder__size-row">${this.sizesFor(s.material).map(([sz, count]) => `
              <button type="button" class="repair-parts-finder__size-btn" data-size="${this.esc(sz)}">
                ${this.sizeLabel(sz)}<small>${count}</small>
              </button>`).join('')}</div>
          </div>`;
      } else if (step === 'type') {
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
              <div>
                <h2>Which schedule? <em>${this.sizeLabel(s.size)} ${this.esc(s.material)}</em></h2>
                <p>Schedule 80 is the thicker, grey pipe; Schedule 40 is the everyday white.</p>
              </div>
              <button type="button" class="repair-parts-finder__back-link" data-goto="size">Change size</button>
            </div>
            <div class="repair-parts-finder__brand-grid">${this.typesFor(s.material, s.size).map(([type, count]) => `
              <button type="button" class="repair-parts-finder__brand-card repair-parts-finder__brand-card--text" data-type="${this.esc(type)}">
                <span class="repair-parts-finder__tile-title">${type === OTHER_TYPE ? 'Other PVC' : this.esc(type)}</span>
                <span class="repair-parts-finder__tile-count">${count} fitting${count === 1 ? '' : 's'}</span>
              </button>`).join('')}</div>
          </div>`;
      } else {
        body = this.renderResults();
      }

      const tools = step === 'results'
        ? `<div class="repair-parts-finder__tools">
            <input class="repair-parts-finder__search-input" type="search" data-filter placeholder="Narrow down — nipple, tee, union, SKU" value="${this.esc(s.filter)}" aria-label="Narrow down these fittings">
            <button type="button" class="repair-parts-finder__reset" data-goto="material">Start over</button>
          </div>`
        : '';

      this.innerHTML = `
        <div class="repair-parts-finder__toolbar">
          <div class="repair-parts-finder__trail" role="group" aria-label="Your fitting">${this.pillsHtml(step)}</div>
          ${tools}
        </div>
        ${body}
        <p class="repair-parts-finder__help">Not sure? <a href="/collections/fittings">Browse all fittings</a> or call <strong>(916) 251-7071</strong>.</p>`;
      this.bind();
    }

    resultsHeading() {
      const s = this.state;
      return `${this.sizeLabel(s.size)} ${this.esc(s.material)}${s.type && s.type !== OTHER_TYPE ? ` ${this.esc(s.type)}` : ''} fittings`;
    }

    resultsBody(results) {
      const s = this.state;
      return results.length
        ? `<ul class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-theme gap-y-8 repair-parts-finder__grid" role="list">${results.map((p) => this.renderCard(p)).join('')}</ul>`
        : `<p class="repair-parts-finder__empty-message">No match${s.filter ? ` for “${this.esc(s.filter)}”` : ''}. We can still source it — <a href="/pages/contact">ask us</a> or call <strong>(916) 251-7071</strong>.</p>`;
    }

    renderResults() {
      const results = this.results();
      return `<div class="repair-parts-finder__results" aria-live="polite">
          <div class="repair-parts-finder__results-header">
            <div>
              <p class="repair-parts-finder__results-kicker">Fittings</p>
              <h2 class="repair-parts-finder__results-title h4">${this.resultsHeading()}</h2>
              <p class="repair-parts-finder__results-meta" data-results-meta>${results.length} fitting${results.length === 1 ? '' : 's'}</p>
            </div>
          </div>
          <div data-results-body>${this.resultsBody(results)}</div>
        </div>`;
    }

    renderCard(p) {
      const url = `/products/${this.esc(p.handle)}`;
      const quote = p.quoteOnly === true;
      const image = p.imageUrl
        ? `<img src="${this.esc(p.imageUrl)}" alt="${this.esc(p.title)}" loading="lazy">`
        : '<div class="repair-parts-finder__card-placeholder" aria-hidden="true"></div>';
      const sku = (p.variants.length === 1 && p.variants[0].sku)
        ? `<p class="repair-parts-finder__card-sku">SKU: ${this.esc(p.variants[0].sku)}</p>` : '';
      const price = quote || !(p.priceMin > 0)
        ? '<p class="repair-parts-finder__card-price repair-parts-finder__card-price--quote">Quote on request</p>'
        : `<p class="repair-parts-finder__card-price">${p.priceMax > p.priceMin ? `From $${Number(p.priceMin).toFixed(2)}` : `$${Number(p.priceMin).toFixed(2)}`}</p>`;
      // sizeLabel() escapes; the rest are escaped here.
      const chips = [this.esc(p.material || ''), (p.diameters || []).map((d) => this.sizeLabel(d)).join(' × '), this.esc(p.pathType || ''), this.esc(p.fittingType || '')]
        .filter(Boolean);
      const chipsHtml = chips.length
        ? `<div class="repair-parts-finder__chips">${chips.map((c) => `<span>${c}</span>`).join('')}</div>` : '';
      return `<li class="repair-parts-finder__item">
          <div class="repair-parts-finder__card">
            <a class="repair-parts-finder__card-main" href="${url}">
              <div class="repair-parts-finder__card-image">${image}</div>
              <div class="repair-parts-finder__card-content">
                ${sku}
                <h3 class="repair-parts-finder__card-title">${this.esc(p.title)}</h3>
                ${chipsHtml}
                ${price}
              </div>
            </a>
            <div class="repair-parts-finder__card-actions">
              ${this.renderAction(p, url, quote)}
              <a class="repair-parts-finder__card-link" href="${url}">View product</a>
            </div>
          </div>
        </li>`;
    }

    renderAction(p, url, quote) {
      if (quote) {
        if (p.variants.length !== 1) return `<a class="btn btn--primary w-full" href="${url}">Choose options to quote</a>`;
        const v = p.variants[0];
        return `<button type="button" class="btn btn--primary w-full js-add-to-quote" data-variant-id="${this.esc(v.variantId)}" data-title="${this.esc(p.title)}" data-sku="${this.esc(v.sku || '')}" data-image="${this.esc(p.imageUrl || '')}" data-handle="${this.esc(p.handle)}">Add to quote</button>`;
      }
      if (!p.available) return '<button class="btn btn--primary w-full" type="button" disabled>Out of stock</button>';
      if (p.variants.length !== 1) return `<a class="btn btn--primary w-full" href="${url}">Choose options</a>`;
      // Minimal markup product-form.js needs: a .js-product-form with a variant
      // id and a [name="add"] button. It AJAX-adds, updates the cart icon and
      // opens the cart drawer — same as the repair-parts finder.
      return `<product-form>
          <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
          <form method="post" action="${this.esc(this.cartAddUrl)}" class="js-product-form" novalidate>
            <input type="hidden" name="id" value="${this.esc(p.variants[0].variantId)}">
            <button class="btn btn--primary w-full" name="add" aria-haspopup="dialog">Add to cart</button>
          </form>
        </product-form>`;
    }

    bind() {
      this.querySelectorAll('[data-material]').forEach((el) => el.addEventListener('click', () =>
        this.setState({ material: el.dataset.material, size: null, type: null, filter: '' })));
      this.querySelectorAll('[data-size]').forEach((el) => el.addEventListener('click', () =>
        this.setState({ size: el.dataset.size, type: null, filter: '' })));
      this.querySelectorAll('[data-type]').forEach((el) => el.addEventListener('click', () =>
        this.setState({ type: el.dataset.type, filter: '' })));
      this.querySelectorAll('[data-goto]:not([disabled])').forEach((el) => el.addEventListener('click', () => {
        const to = el.dataset.goto;
        if (to === 'material') this.setState({ material: null, size: null, type: null, filter: '' });
        else if (to === 'size') this.setState({ size: null, type: null, filter: '' });
        else if (to === 'type') this.setState({ type: null, filter: '' });
      }));
      const filter = this.querySelector('[data-filter]');
      if (filter) {
        // Re-render only the list so the input keeps focus while typing.
        filter.addEventListener('input', () => {
          this.state.filter = filter.value;
          const results = this.results();
          const meta = this.querySelector('[data-results-meta]');
          if (meta) meta.textContent = `${results.length} fitting${results.length === 1 ? '' : 's'}`;
          const count = this.querySelector('.repair-parts-finder__pill--static span');
          if (count) count.textContent = ` (${results.length})`;
          const holder = this.querySelector('[data-results-body]');
          if (holder) holder.innerHTML = this.resultsBody(results);
          this.bindQuote();
        });
      }
      this.bindQuote();
    }

    bindQuote() {
      this.querySelectorAll('.js-add-to-quote:not([data-bound])').forEach((btn) => {
        btn.dataset.bound = '1';
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          document.dispatchEvent(new CustomEvent('quote:add', { detail: {
            variantId: btn.dataset.variantId, title: btn.dataset.title,
            sku: btn.dataset.sku || null, image: btn.dataset.image || null,
            handle: btn.dataset.handle || null, quantity: 1,
          } }));
        });
      });
    }

    /* ---- Utils ---- */

    // App diameters are decimals or whole numbers ("0.75", "1.5", "2").
    sizeValue(s) {
      const clean = String(s).replace(/"/g, '').trim();
      const mixed = clean.match(/^(\d+)\s+(\d+)\/(\d+)$/);
      if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
      const frac = clean.match(/^(\d+)\/(\d+)$/);
      if (frac) return Number(frac[1]) / Number(frac[2]);
      const n = parseFloat(clean);
      return Number.isFinite(n) ? n : 0;
    }

    /** Sizes as fractions (1 ½", not 1.5") — display only; filtering keeps the
        API's value. Mirrors the app's src/lib/size-display.ts. Null = not a size. */
    fractionSize(size) {
      const s = String(size).replace(/["”″]/g, '').trim();
      let v = null;
      const mixed = s.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/);
      const frac = s.match(/^(\d+)\/(\d+)$/);
      if (mixed && Number(mixed[3])) v = Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
      else if (frac && Number(frac[2])) v = Number(frac[1]) / Number(frac[2]);
      else if (/^(\d+\.?\d*|\.\d+)$/.test(s)) v = Number(s);
      if (v === null || !Number.isFinite(v)) return null;
      const n = Math.round(v * 16);
      if (Math.abs(v * 16 - n) > 0.05) return s;
      const whole = Math.floor(n / 16);
      const rest = n % 16;
      if (!rest) return String(whole);
      const gcd = (a, b) => (b ? gcd(b, a % b) : a);
      const d = gcd(rest, 16);
      const key = `${rest / d}/${16 / d}`;
      const glyph = { '1/2': '½', '1/4': '¼', '3/4': '¾', '1/8': '⅛', '3/8': '⅜', '5/8': '⅝', '7/8': '⅞' }[key] || key;
      return whole ? `${whole} ${glyph}` : glyph;
    }

    sizeLabel(s) {
      const f = this.fractionSize(s);
      return f === null ? this.esc(String(s).trim()) : `${this.esc(f)}"`;
    }

    esc(s) {
      return String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }
  customElements.define('fittings-finder', FittingsFinder);
}
